import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type Theme,
  useTheme,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';
import {
  type ChangeEvent,
  type FC,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Row,
  Text,
  MobileInput,
  SwipeComponent,
  MobileSwitch,
  Icon,
} from '@/components/ui';
import { deleteIcon, iconDeleteRed } from '@/assets';
import { EditableCell } from './components';
import { ConfirmModal } from '@/components';

interface Props {
  loading?: boolean;
  sx?: SxProps<Theme>;
  onChangeSiteMaterials: (newMaterialLists: QuantityAndPrice[]) => void;
  onUpdateMaterial?: (materialId: number, quantity: number) => void;
  onDeleteMaterial?: (materialId: number) => Promise<boolean>;
  onDeleteMaterials?: (materialIds: number[]) => Promise<boolean>;
  localSiteMaterials: QuantityAndPrice[];
}
export interface QuantityAndPrice {
  quantity: number;
  id: number;
  materialId: number;
  unitCost: number;
  materialCode: string;
  shortDescription: string;
  unitType: string;
}

// Memoized styles to prevent recreation on every render
const useTableStyles = () => {
  const theme = useTheme();

  return useMemo(() => {
    const baseCell = {
      height: theme.spacing(7.5),
      padding: { xs: theme.spacing(0.5), sm: theme.spacing(1.5) },
      position: 'relative' as const,
    };

    return {
      table: {
        border: '1px solid rgba(224, 224, 224, 1)',
        tableLayout: 'fixed' as const,
        width: '100%',
      },
      headerRow: {
        backgroundColor: theme.palette.grey[50],
        maxWidth: '100%',
      },
      evenRow: {
        backgroundColor: theme.palette.grey[50],
        maxWidth: '100%',
      },
      oddRow: {
        backgroundColor: theme.palette.background.paper,
        maxWidth: '100%',
      },
      summaryRow: {
        fontWeight: 'bold',
      },
      cell: baseCell,
      codeCell: {
        ...baseCell,
        width: { xs: theme.spacing(7), sm: '25%' },
        minWidth: theme.spacing(7),
      },
      descriptionCell: {
        ...baseCell,
        width: { xs: theme.spacing(8), sm: '25%' },
        minWidth: theme.spacing(8),
      },
      priceCell: {
        ...baseCell,
        width: { xs: theme.spacing(7), sm: '15%' },
        minWidth: theme.spacing(7),
      },
      unitCell: {
        ...baseCell,
        width: { xs: theme.spacing(5), sm: '10%' },
        minWidth: theme.spacing(5),
      },
      quantityCell: {
        ...baseCell,
        width: { xs: theme.spacing(6), sm: '20%' },
        minWidth: { xs: theme.spacing(6), sm: theme.spacing(8) },
      },
      totalCell: {
        ...baseCell,
        width: { xs: theme.spacing(8), sm: '15%' },
        minWidth: theme.spacing(8),
      },
      cellText: {
        color: 'secondary.dark',
        fontSize: {
          xs: theme.typography.caption.fontSize,
          sm: theme.typography.body2.fontSize,
        },
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        display: 'block',
        maxWidth: '100%',
      },
      deleteSlideBox: {
        height: '100%',
        position: 'absolute' as const,
        background: theme.palette.error.main,
        top: 0,
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1),
        left: 0,
        '&.slide-in': {
          transform: 'translateX(0)',
          transition: 'transform 0.3s ease-in-out',
        },
        '&.slide-out': {
          transform: 'translateX(-150%)',
          transition: 'transform 0.3s ease-in-out',
        },
      },
    };
  }, [theme]);
};

// Memoized row component to prevent unnecessary re-renders
interface MaterialRowProps {
  item: QuantityAndPrice;
  index: number;
  showDeleteElemId: number | null;
  loading: boolean;
  styles: ReturnType<typeof useTableStyles>;
  theme: Theme;
  onSwiped: (id: number) => void;
  onDeleteMaterial: (id: number) => void;
  onChangeQuantity: (value: number, id: number) => void;
}

const MaterialRow = memo<MaterialRowProps>(
  ({
    item,
    index,
    showDeleteElemId,
    loading,
    styles,
    theme,
    onSwiped,
    onDeleteMaterial,
    onChangeQuantity,
  }) => {
    const { t } = useTranslation();
    const isEven = index % 2 === 0;

    return (
      <TableRow sx={isEven ? styles.evenRow : styles.oddRow}>
        <TableCell
          onClick={() => onSwiped(item.id)}
          sx={[styles.codeCell, { paddingLeft: 0.5 }]}
        >
          <Box
            className={showDeleteElemId === item.id ? 'slide-in' : 'slide-out'}
            sx={styles.deleteSlideBox}
            onClick={() => onDeleteMaterial(item.id)}
          >
            <Box sx={{ height: theme.spacing(2.25), mx: theme.spacing(0.5) }}>
              <Icon src={deleteIcon} />
            </Box>
            <Text sx={{ color: 'white' }}>{t('delete_material_table')}</Text>
          </Box>
          <SwipeComponent swiped={() => onSwiped(item.id)}>
            <Text sx={{ ...styles.cellText, paddingLeft: theme.spacing(0.5) }}>
              {item.materialCode || 'N/A'}
            </Text>
          </SwipeComponent>
        </TableCell>
        <TableCell
          onClick={() => onSwiped(item.id)}
          sx={styles.descriptionCell}
        >
          <SwipeComponent swiped={() => onSwiped(item.id)}>
            <Text sx={styles.cellText}>{item.shortDescription || 'N/A'}</Text>
          </SwipeComponent>
        </TableCell>
        <TableCell sx={styles.priceCell}>
          <Text sx={styles.cellText}>₪{item.unitCost.toFixed(2)}</Text>
        </TableCell>
        <TableCell onClick={() => onSwiped(item.id)} sx={styles.unitCell}>
          <SwipeComponent swiped={() => onSwiped(item.id)}>
            <Text sx={styles.cellText}>{item.unitType || 'N/A'}</Text>
          </SwipeComponent>
        </TableCell>
        <TableCell
          sx={[styles.quantityCell, { paddingLeft: theme.spacing(0.5) }]}
        >
          <EditableCell
            value={item.quantity}
            loading={loading}
            onChange={value => onChangeQuantity(value, item.materialId)}
          />
        </TableCell>
        <TableCell sx={styles.totalCell}>
          <Text sx={{ ...styles.cellText, fontWeight: 'bold' }}>
            ₪{(item.quantity * item.unitCost).toFixed(2)}
          </Text>
        </TableCell>
      </TableRow>
    );
  }
);

export const MaterialsAndPricingTable: FC<Props> = ({
  onChangeSiteMaterials,
  onUpdateMaterial,
  onDeleteMaterial,
  onDeleteMaterials,
  localSiteMaterials,
  loading,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useTableStyles();
  const [searchText, setSearchText] = useState<string>('');
  const [showDeleteElemId, setShowDeleteElemId] = useState<number | null>(null);
  const [showAllMaterials, setShowAllMaterials] = useState<boolean>(true);

  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [showModalDeleteRows, setShowModalDeleteRows] = useState(false);

  const [materialDeleteId, setMaterialDeleteId] = useState<number | null>(null);

  const onChangeQuantity = (value: number, id: number) => {
    // Use new single material update if available
    if (onUpdateMaterial) {
      onUpdateMaterial(id, value);
    } else {
      // Fallback to old method
      const newLocalSiteMaterials = [...localSiteMaterials];
      const index = newLocalSiteMaterials.findIndex(elem => elem.id === id);
      newLocalSiteMaterials[index].quantity = value;
      onChangeSiteMaterials(newLocalSiteMaterials);
    }
  };

  const onChangeSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  // Optimized filtering and sorting with single pass through data
  const filteredQuantityAndPrice: QuantityAndPrice[] = useMemo(() => {
    let filtered = localSiteMaterials;

    // Apply quantity filter first
    if (!showAllMaterials) {
      filtered = filtered.filter(item => item.quantity > 0);
    }

    // Apply search filter with optimized search logic
    const trimmedSearch = searchText.trim().toLowerCase();
    if (trimmedSearch) {
      filtered = filtered.filter(
        item =>
          item.materialCode?.toLowerCase().includes(trimmedSearch) ||
          item.shortDescription?.toLowerCase().includes(trimmedSearch) ||
          item.unitType?.toLowerCase().includes(trimmedSearch)
      );
    }

    // Sort once at the end
    return filtered.sort((a, b) =>
      (a.shortDescription || '').localeCompare(b.shortDescription || '')
    );
  }, [searchText, showAllMaterials, localSiteMaterials]);

  // Memoize summary calculations
  const summaryData = useMemo(
    () => ({
      totalQuantity: filteredQuantityAndPrice.reduce(
        (sum, item) => sum + item.quantity,
        0
      ),
      totalPrice: filteredQuantityAndPrice.reduce(
        (sum, item) => sum + item.quantity * item.unitCost,
        0
      ),
    }),
    [filteredQuantityAndPrice]
  );

  const handleSwiped = useCallback(
    (id: number) => {
      if (loading) return;
      if (showDeleteElemId === id) {
        setShowDeleteElemId(null);
        return;
      }
      setShowDeleteElemId(id);
    },
    [loading, showDeleteElemId]
  );

  const deleteMaterial = useCallback(
    async (id: number) => {
      const material = localSiteMaterials.find(item => item.id === id);

      if (material && onDeleteMaterial) {
        // Use new API to delete material
        const success = await onDeleteMaterial(material.materialId);
        if (success) {
          setShowDeleteElemId(null);
          setShowModalConfirm(false);
          setMaterialDeleteId(null);
        }
      } else {
        // Fallback to old method
        const newLocalSiteMaterials: QuantityAndPrice[] = [
          ...localSiteMaterials,
        ];
        const index = newLocalSiteMaterials.findIndex(item => item.id === id);
        newLocalSiteMaterials.splice(index, 1);
        onChangeSiteMaterials(newLocalSiteMaterials);
        setShowDeleteElemId(null);
        setShowModalConfirm(false);
        setMaterialDeleteId(null);
      }
    },
    [localSiteMaterials, onDeleteMaterial, onChangeSiteMaterials]
  );

  const handleDeleteMaterial = useCallback((id: number) => {
    setMaterialDeleteId(id);
    setShowModalConfirm(true);
  }, []);

  const cancelDeleteMaterial = useCallback(() => {
    setMaterialDeleteId(null);
    setShowModalConfirm(false);
  }, []);

  const onChangeShaw = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setShowAllMaterials(checked);
    //localStorageService.showAllMaterials = checked;
    //const [showAllMaterials, setShowAllMaterials] = useState<boolean>(false);
  };
  const openModalDeleteRows = () => {
    setShowModalDeleteRows(true);
  };

  const cancelDeleteRows = () => {
    setShowModalDeleteRows(false);
  };
  const deleteRows = async () => {
    // Find materials with quantity === 0
    const materialsToDelete = localSiteMaterials.filter(
      item => item.quantity === 0
    );

    if (materialsToDelete.length === 0) {
      setShowModalDeleteRows(false);
      return;
    }

    const materialIdsToDelete = materialsToDelete.map(item => item.materialId);

    if (onDeleteMaterials) {
      // Use new bulk delete API
      const success = await onDeleteMaterials(materialIdsToDelete);
      if (success) {
        // Update local state - remove deleted materials
        const newLocalSiteMaterials = localSiteMaterials.filter(
          item => item.quantity !== 0
        );
        onChangeSiteMaterials(newLocalSiteMaterials);
      }
    } else {
      // Fallback to old method - just filter locally
      const newLocalSiteMaterials = localSiteMaterials.filter(
        item => item.quantity !== 0
      );
      onChangeSiteMaterials(newLocalSiteMaterials);
    }

    setShowModalDeleteRows(false);
  };

  return (
    <Box maxWidth={'100%'} sx={{ opacity: loading ? 0.5 : 1 }}>
      <Text
        sx={{ color: theme.palette.text.primary, mt: theme.spacing(2) }}
      >{`${t('materials_and_pricing_table')}`}</Text>
      <MobileSwitch
        value={showAllMaterials}
        onChange={onChangeShaw}
        title={`${t('show_all_materials_pricing_table')}`}
        titleSelected={`${t('hide_unused_materials_pricing_table')}`}
      />

      <Row sx={{ cursor: 'pointer' }} onClick={openModalDeleteRows}>
        <Icon src={iconDeleteRed} />
        <Box sx={{ mx: theme.spacing(1) }}>
          {t('delete_rows_without_quantity')}
        </Box>
      </Row>

      <MobileInput
        label={``}
        name='requiredChanges'
        error={false}
        helperText={''}
        onChange={value => onChangeSearch(value.target.value)}
        value={searchText}
        search
        placeholder={t('materials_and_pricing_table_placeholder')}
      />
      <Box>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow sx={styles.headerRow}>
              <TableCell
                sx={[styles.codeCell, { paddingLeft: theme.spacing(0.5) }]}
              >
                <Text sx={styles.cellText}>{t('code_material_table')}</Text>
              </TableCell>
              <TableCell sx={styles.descriptionCell}>
                <Text sx={styles.cellText}>
                  {t('description_material_table')}
                </Text>
              </TableCell>
              <TableCell sx={styles.priceCell}>
                <Text sx={styles.cellText}>{t('price_material_table')}</Text>
              </TableCell>
              <TableCell sx={styles.unitCell}>
                <Text sx={styles.cellText}>{t('unit_material_table')}</Text>
              </TableCell>
              <TableCell
                sx={[styles.quantityCell, { paddingLeft: theme.spacing(0.5) }]}
              >
                <Text sx={styles.cellText}>{t('qty_material_table')}</Text>
              </TableCell>
              <TableCell sx={styles.totalCell}>
                <Text sx={styles.cellText}>
                  {t('total_price_material_table')}
                </Text>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQuantityAndPrice.map((item, index) => (
              <MaterialRow
                key={item.materialId}
                item={item}
                index={index}
                showDeleteElemId={showDeleteElemId}
                loading={!!loading}
                styles={styles}
                theme={theme}
                onSwiped={handleSwiped}
                onDeleteMaterial={handleDeleteMaterial}
                onChangeQuantity={onChangeQuantity}
              />
            ))}
            {/* Summary Row */}
            <TableRow sx={styles.summaryRow}>
              <TableCell colSpan={2} sx={styles.cell}>
                {/* Empty cell - no text needed at the beginning */}
              </TableCell>
              <TableCell sx={styles.cell}>
                {/* Price column - empty for summary */}
              </TableCell>
              <TableCell sx={styles.cell}>
                {/* Unit column - empty for summary */}
              </TableCell>
              <TableCell sx={styles.cell}>
                <Text
                  sx={{
                    ...styles.cellText,
                    fontWeight: 'bold',
                    fontSize: {
                      xs: theme.typography.overline.fontSize,
                      sm: theme.typography.caption.fontSize,
                    },
                  }}
                >
                  {summaryData.totalQuantity}
                </Text>
              </TableCell>
              <TableCell sx={styles.cell}>
                <Text
                  sx={{
                    ...styles.cellText,
                    fontWeight: 'bold',
                    fontSize: {
                      xs: theme.typography.overline.fontSize,
                      sm: theme.typography.caption.fontSize,
                    },
                    mb: theme.spacing(0.25),
                  }}
                >
                  {t('total_price_summary')}
                </Text>
                <Text
                  sx={{
                    ...styles.cellText,
                    fontWeight: 'bold',
                    fontSize: {
                      xs: theme.typography.caption.fontSize,
                      sm: theme.typography.body2.fontSize,
                    },
                  }}
                >
                  ₪{summaryData.totalPrice.toFixed(2)}
                </Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Confirm delete material modal */}
      <ConfirmModal
        cancel={cancelDeleteMaterial}
        confirm={() => deleteMaterial(materialDeleteId as number)}
        title={t('are_you_shure_delete_material')}
        show={showModalConfirm}
      />
      <ConfirmModal
        cancel={cancelDeleteRows}
        confirm={deleteRows}
        title={t('are_you_shure_delete_rows_without_quantity')}
        show={showModalDeleteRows}
      />
    </Box>
  );
};
